import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiErrorMessage } from '../../components/ErrorMessage';
import { Constants } from '../../constants';
import {
  DataTable,
  ColumnDefinition,
} from '../../components/common/DataTable/DataTable';
import { useClearings } from './clearingsQueries';
import { useClearingActions } from './useClearingActions';
import { Clearing } from '../../types/Clearing';

export interface ClearingsTableProps {
  filters: Metadata;
  header?: string;
  trackingContext: string;
}

export function ClearingsTable({
  filters,
  header,
  trackingContext,
}: ClearingsTableProps): JSX.Element {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const { data, error, isLoading, refetch } = useClearings({
    page,
    per_page: Constants.LARGE_PAGE_SIZE,
    ...filters,
  });

  const { actions } = useClearingActions(trackingContext);

  const tableColumns: ColumnDefinition[] = [
    {
      field: 'id',
      type: 'string',
      headerName: 'ID de liquidación',
    },
    {
      field: 'created_at',
      type: 'date',
      headerName: 'Fecha de liquidación',
    },
    {
      field: 'gross_amount',
      type: 'currency',
      headerName: 'Monto Bruto',
      rightAligned: true,
    },
    {
      field: 'discounts_amount',
      type: 'currency',
      headerName: 'Monto Descuentos',
      rightAligned: true,
    },
    {
      field: 'chargebacks_amount',
      type: 'currency',
      headerName: 'Monto Contracargos',
      rightAligned: true,
    },
    {
      field: 'net_amount',
      type: 'currency',
      headerName: 'Monto Neto',
      rightAligned: true,
    },
    {
      field: 'status',
      type: 'tag',
      headerName: t('common.status'),
      tagType: 'clearing',
      tagLabel: (clearing: Clearing) => {
        return t(`data.clearing.status.${clearing.status}`);
      },
    },
  ];

  const loadPage = (pageNumber: number) => setPage(pageNumber);

  const onErrorMessageClick = () => refetch();

  if (error) {
    return <ApiErrorMessage error={error} onClick={onErrorMessageClick} />;
  }

  const hasData = data?.data ? data?.data.length > 0 : false;

  return (
    <DataTable
      tableProps={{
        hasData,
        noDataMessage:
          'No se encontraron liquidaciones con los filtros aplicados',
      }}
      columns={tableColumns}
      pageSize={Constants.LARGE_PAGE_SIZE}
      isLoading={isLoading}
      rows={data?.data}
      menuActions={actions}
      rowBorder
      paginatorProps={{
        pages: data?.total_pages,
        selected: Number(data?.current_page),
        onSelectPage: loadPage,
      }}
      tableHeader={header}
    />
  );
}
